import { type ReactNode, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CAlert, CBadge, CButton, CCard, CCardBody, CCol, CRow, CSpinner } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilBadge, cilEqualizer, cilReload } from "@coreui/icons";
import { canResumeMembership, useMyMemberships } from "../../api/hooks/useMemberships";
import { useMyMembershipRequests } from "../../api/hooks/useMembershipRequests";
import { isExternalTier, useMembershipTiers } from "../../api/hooks/useMembershipTiers";
import { usePlatformAccounts } from "../../api/hooks/usePlatformAccounts";
import { useTradingStrategies } from "../../api/hooks/useTradingStrategies";
import { formatDateShort } from "../../lib/dates";
import { membershipEndPath, membershipResumePath, requestMembershipPath, routePaths } from "../../routes/route-paths";
import { IncludedStrategyCard } from "../trading-strategies/components/IncludedStrategyCard";
import { MembershipBadge } from "../trading-strategies/components/MembershipBadge";
import { groupSides } from "../trading-strategies/strategySides";
import { MembershipBuyPanel } from "./MembershipBuyPanel";
import { tierStandings } from "./membership-state";
import { TierStrategyCard } from "./TierStrategyCard";

// Two rows on a wide screen, as on My strategies, so a tier of dozens of strategies doesn't bury the page.
const PREVIEW_COUNT = 6;

/**
 * One membership: where the customer stands with it, and the strategies it includes. Each card on Memberships
 * opens it, and its header holds the one thing to do next.
 */
export function MembershipDetails() {
  const { tierId = "" } = useParams<{ tierId: string }>();
  const { t } = useTranslation("memberships");
  const tiers = useMembershipTiers();
  const memberships = useMyMemberships();
  const requests = useMyMembershipRequests();
  const accounts = usePlatformAccounts();
  const strategies = useTradingStrategies();
  const [showAll, setShowAll] = useState(false);

  const isLoading = tiers.isLoading || memberships.isLoading || requests.isLoading || strategies.isLoading;
  const loadError = tiers.error ?? memberships.error ?? requests.error ?? strategies.error;

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (loadError) {
    return <CAlert color="danger">{t("detail.loadError")}</CAlert>;
  }

  const tier = tiers.data?.find((candidate) => candidate.id === tierId);
  // Only live tiers are listed, so a retired one the customer still holds is drawn from the membership.
  const held = memberships.data?.find((membership) => membership.membershipTierId === tierId);
  const request = requests.data?.find((candidate) => candidate.membershipTierId === tierId);

  if (!tier && !held) {
    return (
      <CAlert color="info">
        {t("detail.notFound")} <Link to={routePaths.memberships}>{t("detail.browse")}</Link>
      </CAlert>
    );
  }

  const standing = tierStandings(memberships.data ?? [], requests.data ?? [])(tierId);
  const tierName = tier?.name ?? held!.tierName;
  const platformName = tier?.platform.name ?? held!.platformName;
  const badge = tier
    ? { badgeColour: tier.platform.badgeColour, badgeLetters: tier.platform.badgeLetters, platformName }
    : held!;
  const strategyIds = tier?.tradingStrategyIds ?? held!.tradingStrategyIds;
  const includedSides = (strategies.data ?? []).filter((strategy) => strategyIds.includes(strategy.id));
  const included = groupSides(includedSides);
  const visible = showAll ? included : included.slice(0, PREVIEW_COUNT);
  const savedAccount = tier
    ? accounts.data?.find((account) => account.platformId === tier.platform.id)?.account
    : undefined;

  let pill: ReactNode = null;
  let action: ReactNode = null;
  const facts: { label: string; value: ReactNode }[] = [];
  const accountLabel = t("detail.account", { platform: platformName });

  if (held) {
    pill = held.activeTo ? (
      <CBadge color="warning" shape="rounded-pill">
        {t("status.endsOn", { date: formatDateShort(held.activeTo) })}
      </CBadge>
    ) : (
      <CBadge color="success" shape="rounded-pill">
        {t("status.active")}
      </CBadge>
    );
    action = (
      <div className="d-flex flex-wrap gap-2">
        <Link to={routePaths.signalsConfigure} className="btn btn-primary">
          <CIcon icon={cilEqualizer} className="me-2" />
          {t("detail.configure")}
        </Link>
        {tier && !isExternalTier(tier) && (
          <Link to={routePaths.billing} className="btn btn-outline-secondary">
            {t("detail.billing")}
          </Link>
        )}
        {held.boughtHere && held.activeTo === null && (
          <Link to={membershipEndPath(tierId)} className="btn btn-outline-danger">
            {t("detail.end")}
          </Link>
        )}
        {canResumeMembership(held) && (
          <Link to={membershipResumePath(tierId)} className="btn btn-success">
            {t("detail.resume")}
          </Link>
        )}
      </div>
    );
    if (savedAccount) {
      facts.push({ label: accountLabel, value: savedAccount });
    }
  } else if (standing === "pending" && request) {
    pill = (
      <CBadge color="warning" shape="rounded-pill">
        {t("status.pending")}
      </CBadge>
    );
    facts.push(
      { label: t("detail.sent"), value: formatDateShort(request.requestedAt) },
      { label: accountLabel, value: request.platformAccount },
      { label: t("detail.next"), value: t("detail.nextPending") },
    );
  } else if (standing === "declined" && request) {
    pill = (
      <CBadge color="danger" shape="rounded-pill">
        {t("status.declined")}
      </CBadge>
    );
    action = (
      <Link to={requestMembershipPath(tierId)} className="btn btn-primary">
        <CIcon icon={cilReload} className="me-2" />
        {t("detail.sendAgain")}
      </Link>
    );
    facts.push(
      { label: t("detail.declinedOn"), value: formatDateShort(request.decidedAt ?? request.requestedAt) },
      { label: t("detail.why"), value: request.declineReason },
    );
  } else if (tier && isExternalTier(tier)) {
    action = (
      <Link to={requestMembershipPath(tierId)} className="btn btn-primary">
        <CIcon icon={cilBadge} className="me-2" />
        {t("detail.claim")}
      </Link>
    );
  } else if (tier) {
    action = <MembershipBuyPanel tier={tier} />;
    facts.push({ label: t("detail.starts"), value: t("detail.startsWhenPaid") });
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-4">
        <div style={{ minWidth: 0 }}>
          <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
            <MembershipBadge membership={badge} size={30} />
            <h1 className="mb-0">
              {platformName} · {tierName}
            </h1>
            {pill}
          </div>
          {tier && <p className="text-body-secondary mb-0">{tier.description}</p>}
        </div>
        {action && <div className="col-12 col-sm-auto d-grid">{action}</div>}
      </div>

      {facts.length > 0 && (
        <CCard className={`mb-4${standing === "declined" ? " border-danger" : ""}`}>
          <CCardBody className="d-flex flex-wrap gap-4">
            {facts.map((fact) => (
              <div key={fact.label} style={{ minWidth: 0 }}>
                <div className="small text-uppercase fw-semibold text-body-secondary">{fact.label}</div>
                <div className="text-break">{fact.value}</div>
              </div>
            ))}
          </CCardBody>
        </CCard>
      )}

      <div className="d-flex justify-content-between align-items-baseline flex-wrap gap-2 mb-3">
        <h2 className="h4 mb-0">{t("detail.includes")}</h2>
        <span className="small text-body-secondary">{t("browse.strategies", { count: included.length })}</span>
      </div>

      {included.length === 0 && <p className="text-body-secondary">{t("detail.empty")}</p>}

      <CRow xs={{ gutter: 4 }}>
        {visible.map((sides) => (
          <CCol key={sides[0].id} md={6} xl={4}>
            {held ? (
              <IncludedStrategyCard
                sides={sides}
                memberships={(memberships.data ?? []).filter((membership) =>
                  sides.some((side) => membership.tradingStrategyIds.includes(side.id)),
                )}
              />
            ) : (
              <TierStrategyCard sides={sides} tierName={tierName} />
            )}
          </CCol>
        ))}
      </CRow>

      {!showAll && included.length > PREVIEW_COUNT && (
        <div className="d-flex justify-content-center mt-4">
          <CButton color="primary" variant="outline" onClick={() => setShowAll(true)}>
            {t("detail.showAll", { count: included.length })}
          </CButton>
        </div>
      )}
    </div>
  );
}

export default MembershipDetails;
